/**
 * Navigation personalisation store — Favoris (★ pins) + Récents.
 *
 * Persisted per browser via zustand `persist`. The storage adapter is wrapped in
 * try/catch so private-mode / blocked-storage / quota errors never break the
 * shell (the lists simply live in memory for the session).
 */

import { create } from 'zustand';
import { createJSONStorage, persist, type StateStorage } from 'zustand/middleware';

export const MAX_FAVORITES = 12;
export const MAX_RECENTS = 6;

const safeStorage: StateStorage = {
  getItem: (name) => {
    try {
      return localStorage.getItem(name);
    } catch {
      return null;
    }
  },
  setItem: (name, value) => {
    try {
      localStorage.setItem(name, value);
    } catch {
      /* ignore quota / private-mode failures */
    }
  },
  removeItem: (name) => {
    try {
      localStorage.removeItem(name);
    } catch {
      /* ignore */
    }
  },
};

interface NavState {
  /** Pinned routes, in pin order. */
  favorites: string[];
  /** Recently visited routes, most recent first. */
  recents: string[];
  isFavorite: (to: string) => boolean;
  toggleFavorite: (to: string) => void;
  pushRecent: (to: string) => void;
  clearRecents: () => void;
}

const strings = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];

export const useNavStore = create<NavState>()(
  persist(
    (set, get) => ({
      favorites: [],
      recents: [],
      isFavorite: (to) => get().favorites.includes(to),
      toggleFavorite: (to) =>
        set((s) =>
          s.favorites.includes(to)
            ? { favorites: s.favorites.filter((x) => x !== to) }
            : { favorites: [...s.favorites, to].slice(-MAX_FAVORITES) },
        ),
      pushRecent: (to) =>
        set((s) =>
          s.recents[0] === to
            ? s
            : { recents: [to, ...s.recents.filter((x) => x !== to)].slice(0, MAX_RECENTS) },
        ),
      clearRecents: () => set({ recents: [] }),
    }),
    {
      name: 'psn-nav-personal',
      version: 1,
      storage: createJSONStorage(() => safeStorage),
      partialize: (s) => ({ favorites: s.favorites, recents: s.recents }),
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<NavState>;
        return { ...current, favorites: strings(p.favorites), recents: strings(p.recents) };
      },
    },
  ),
);
